import React, { useEffect, useState } from "react";
import { PageHeader, KpiCard, DataTable } from "@/components/common";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Legend } from "recharts";
import { TrendingUp, Building2, Globe, MousePointerClick } from "lucide-react";

const fmt = new Intl.NumberFormat("tr-TR");
const pct = (n) => `${(n || 0).toFixed(2)}%`;

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/reports/summary");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-slate-200 rounded"></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[0,1,2,3].map(i => <div key={i} className="h-28 bg-white border border-slate-200 rounded-md" />)}
        </div>
        <div className="h-72 bg-white border border-slate-200 rounded-md" />
      </div>
    );
  }

  const daily = (data.daily || []).map((d) => ({
    date: d.date.slice(5),
    Gösterim: d.impressions,
    Görülebilir: d.viewable_impressions,
    Tıklama: d.clicks,
  }));

  return (
    <div className="space-y-8">
      <PageHeader
        overline="Genel Bakış"
        title="Gösterge Paneli"
        description={`Son 30 gün özet metrikleri (${data.range?.start} → ${data.range?.end})`}
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Toplam Belediye" value={fmt.format(data.counts.municipalities)} sub={<span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> Aktif</span>} testid="kpi-municipalities" />
        <KpiCard label="Aktif Yayıncı Sitesi" value={fmt.format(data.counts.publisher_sites)} sub={<span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> {fmt.format(data.counts.active_placements)} aktif yerleşim</span>} testid="kpi-sites" />
        <KpiCard label="Bugünkü Gösterim" value={fmt.format(data.today.impressions)} sub={<span className="inline-flex items-center gap-1 text-emerald-600"><TrendingUp className="w-3 h-3" /> Anlık</span>} testid="kpi-today-impr" accent="red" />
        <KpiCard label="Bugünkü Tıklama · TO" value={fmt.format(data.today.clicks)} sub={<span className="inline-flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> TO: {pct(data.today.ctr)}</span>} testid="kpi-today-clicks" />
      </div>

      {/* Chart */}
      <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="dashboard-trend-chart">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">30 Günlük Trend</div>
            <div className="font-heading font-semibold text-lg text-slate-950">Gösterim ve Tıklama</div>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-600">
            <span>Toplam: <b className="font-heading">{fmt.format(data.totals.impressions)}</b> gösterim · <b className="font-heading">{fmt.format(data.totals.clicks)}</b> tıklama · TO: <b className="font-heading">{pct(data.totals.ctr)}</b></span>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={daily} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="Gösterim" stroke="#0f172a" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Görülebilir" stroke="#16a34a" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Tıklama" stroke="#dc2626" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top municipalities + sites */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="mb-3">
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">En İyi Performanslı</div>
            <div className="font-heading font-semibold text-lg text-slate-950">Belediyeler</div>
          </div>
          <DataTable
            testid="top-municipalities-table"
            columns={[
              { label: "Belediye", render: (r) => <span className="font-medium">{r.municipality_name}</span> },
              { label: "Gösterim", render: (r) => fmt.format(r.impressions) },
              { label: "Tıklama", render: (r) => fmt.format(r.clicks) },
              { label: "TO", render: (r) => pct(r.ctr) },
            ]}
            rows={data.top_municipalities}
          />
        </div>
        <div>
          <div className="mb-3">
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">En İyi Performanslı</div>
            <div className="font-heading font-semibold text-lg text-slate-950">Yayıncı Siteler</div>
          </div>
          <DataTable
            testid="top-sites-table"
            columns={[
              { label: "Site", render: (r) => <div><div className="font-medium">{r.site_name}</div><div className="text-xs text-slate-500 font-mono">{r.site_domain}</div></div> },
              { label: "Gösterim", render: (r) => fmt.format(r.impressions) },
              { label: "Tıklama", render: (r) => fmt.format(r.clicks) },
              { label: "TO", render: (r) => pct(r.ctr) },
            ]}
            rows={data.top_sites}
          />
        </div>
      </div>
    </div>
  );
}
